import {
  AgentThinkingStep,
  AgentThinkingStepKind,
  ChatMessage,
} from '@/types/chat';

export type AgentThinkingEvent =
  | { type: 'status'; label: string }
  | { type: 'tool_start'; toolName: string; toolUseId: string }
  | { type: 'tool_result'; toolUseId: string; label?: string }
  | { type: 'complete_status' }
  | { type: 'complete' };

export const formatAgentToolName = (toolName: string) =>
  toolName.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

const completeSteps = (
  steps: AgentThinkingStep[],
  now: number,
  predicate: (step: AgentThinkingStep) => boolean
): AgentThinkingStep[] =>
  steps.map(step =>
    step.state === 'running' && predicate(step)
      ? { ...step, state: 'done', endedAt: now }
      : step
  );

const isKind = (kind: AgentThinkingStepKind) => (step: AgentThinkingStep) =>
  step.kind === kind;

export function applyAgentThinkingEvent(
  steps: AgentThinkingStep[] | undefined,
  event: AgentThinkingEvent,
  now = Date.now()
): AgentThinkingStep[] {
  const current = steps ?? [];

  switch (event.type) {
    case 'status': {
      const last = current[current.length - 1];
      if (
        last?.state === 'running' &&
        last.kind === 'status' &&
        last.label === event.label
      ) {
        return current;
      }

      return [
        ...completeSteps(current, now, isKind('status')),
        {
          id: `status-${now}-${current.length}`,
          kind: 'status',
          label: event.label,
          state: 'running',
          startedAt: now,
        },
      ];
    }
    case 'tool_start': {
      const alreadyRunning = current.some(
        step =>
          step.state === 'running' &&
          step.kind === 'tool' &&
          step.toolUseId === event.toolUseId
      );
      if (alreadyRunning) {
        return current;
      }

      return [
        ...completeSteps(current, now, () => true),
        {
          id: event.toolUseId,
          kind: 'tool',
          label: `Using ${formatAgentToolName(event.toolName)}`,
          state: 'running',
          toolName: event.toolName,
          toolUseId: event.toolUseId,
          startedAt: now,
        },
      ];
    }
    case 'tool_result': {
      const hasStep = current.some(step => step.toolUseId === event.toolUseId);
      if (!hasStep) {
        return [
          ...completeSteps(current, now, isKind('status')),
          {
            id: event.toolUseId,
            kind: 'tool',
            label: event.label ?? 'Tool',
            state: 'done',
            toolUseId: event.toolUseId,
            startedAt: now,
            endedAt: now,
          },
        ];
      }

      return current.map(step =>
        step.state === 'running' &&
        step.kind === 'tool' &&
        step.toolUseId === event.toolUseId
          ? {
              ...step,
              state: 'done' as const,
              endedAt: now,
              ...(event.label ? { label: event.label } : {}),
            }
          : step
      );
    }
    case 'complete_status':
      return completeSteps(current, now, isKind('status'));
    case 'complete':
      return completeSteps(current, now, () => true);
    default: {
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
}

export function initialThinkingSteps(label: string): AgentThinkingStep[] {
  return applyAgentThinkingEvent([], { type: 'status', label });
}

export function patchAssistantThinking(
  current: ChatMessage,
  event: AgentThinkingEvent,
  extra: Partial<ChatMessage> = {}
): ChatMessage {
  return patchAssistantThinkingMany(current, [event], extra);
}

export function patchAssistantThinkingMany(
  current: ChatMessage,
  events: AgentThinkingEvent[],
  extra: Partial<ChatMessage> = {}
): ChatMessage {
  return {
    ...current,
    ...extra,
    thinkingSteps: events.reduce(
      (steps, event) => applyAgentThinkingEvent(steps, event),
      current.thinkingSteps
    ),
  };
}
